import { memo, useEffect, useRef } from "react";

import Box from "ds/components/Box";
import ListEntitiesItemDescription from "components/ListEntitiesItem/Description";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import ListEntitiesItem from "components/ListEntitiesItem";
import ModuleVersionStateBadge from "components/ModuleVersionStateBadge";
import TagsList from "components/TagsList";
import { replaceSubdomain } from "utils/history";
import CopyFieldIcon from "components/CopyField/Icon";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import { User } from "components/icons/generated";

import { SharedModuleData } from "../../gql";
import styles from "./styles.module.css";

type ModuleListItemProps = {
  module: SharedModuleData;
  setRowHeight?: (size: number) => void;
};

const ModuleListItem = (props: ModuleListItemProps) => {
  const { module, setRowHeight } = props;

  const rowRef = useRef<HTMLDivElement>(null);

  const moduleOrigin = replaceSubdomain(window.location.origin, module.ownerSubdomain);

  const moduleUrl = `${moduleOrigin}/module/${module.id}`;

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  // on every render, update the row height
  useEffect(handleRowHeight);

  const moduleLatestVersionUrl = module.latest ? `${moduleUrl}/version/latest` : null;

  return (
    <ListEntitiesItem ref={rowRef} className={styles.listItem} direction="column" gap="small">
      <Box justify="between">
        <Box gap="medium" align="center">
          <ListEntitiesItemLink href={moduleUrl} title={module.name} />

          <ModuleVersionStateBadge
            state={module.current?.state}
            number={module.current?.number}
            yanked={module.current?.yanked}
          />
        </Box>

        <CopyFieldIcon title="Copy ID" value={module.id} />
      </Box>

      <Box direction="column" gap="medium">
        {module.description && (
          <ListEntitiesItemDescription
            className={styles.moduleDescription}
            description={module.description}
            id={module.id}
          />
        )}
        <MetaInfoList>
          <MetaInfoListItem icon={User} tooltip="Owner">
            {module.ownerSubdomain}
          </MetaInfoListItem>

          {moduleLatestVersionUrl && (
            <MetaInfoListItem
              linkText="See the latest stable version"
              href={moduleLatestVersionUrl}
            />
          )}
        </MetaInfoList>
      </Box>

      <TagsList tags={module.labels} onExpand={handleRowHeight} />
    </ListEntitiesItem>
  );
};

export default memo(ModuleListItem);
